import { z } from 'zod';

import {
  EMPLOYEE_SAVINGS_BRACKETS_MAX_NUM,
  EMPLOYEE_SAVINGS_BRACKETS_MIN_NUM,
} from '@Common/constants/employee_savings';
import { B2bCashSourceValues, ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import { ZodB2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts/campaign_action_handler';
import { ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodMatchingContributionLimit = z.union([
  z.object({
    type: z.literal('absolute_amount'),
    amount: z.number().nonnegative(),
  }),
  z.object({
    type: z.literal('percentage_of_legal_cap'),
    percentage: z.number().min(0).max(16),
  }),
]);
export type MatchingContributionLimit = z.TypeOf<typeof ZodMatchingContributionLimit>;

const ZodLimitValueSchema = z.object({
  contribution_pct: z
    .number()
    .positive(
      'contribution_pct should be a positive value, if you want to set it to 0 just remove the corresponding cash source from limits_per_contribution_type.',
    ),
  limit: ZodMatchingContributionLimit,
});

const ZodMatchingContributionConfigSimple = z.object({
  type: z.literal('simple'),
  limits_per_contribution_type: z
    .object(Object.fromEntries(B2bCashSourceValues.map((key) => [key, ZodLimitValueSchema])))
    .partial()
    .refine((data) => Object.keys(data).length > 0, {
      message: 'must have at least one contribution type',
    }),
});

export type MatchingContributionConfigSimple = z.TypeOf<typeof ZodMatchingContributionConfigSimple>;

const ZodSupportedContributionTypes = z
  .array(ZodB2bCashSource)
  .min(1)
  .refine((data) => new Set(data).size === data.length, {
    message: 'must have unique values',
  });

const ZodLimitsPerCSPCode = z
  .record(
    ZodTrimmedString,
    z.object({
      contribution_pct: z.number().min(0),
      limit: ZodMatchingContributionLimit,
    }),
  )
  .refine((data) => Object.keys(data).length > 0, {
    message: 'must have at least one contribution type',
  })
  .refine(
    (data) =>
      Object.entries(data).every(([key, value]) => key === '' || value.contribution_pct >= 0.01),
    'Contribution pct should be superior or equals than 0.01',
  );

const ZodMatchingContributionConfigCSPCode = z.object({
  type: z.literal('csp_code'),
  supported_contribution_types: ZodSupportedContributionTypes,
  limits_per_csp_code: ZodLimitsPerCSPCode,
});

export type MatchingContributionConfigCSPCode = z.TypeOf<
  typeof ZodMatchingContributionConfigCSPCode
>;

const ZodMatchingContributionConfigCSPCodePerCashSource = z.object({
  type: z.literal('csp_code_per_cash_source'),
  supported_contribution_types: z
    .array(
      z.object({
        cash_source: ZodB2bCashSource,
        limits_per_csp_code: ZodLimitsPerCSPCode,
      }),
    )
    .min(1)
    .refine((data) => new Set(data.map((entry) => entry.cash_source)).size === data.length, {
      message: 'must have unique values',
    }),
});

export type MatchingContributionConfigCSPCodePerCashSource = z.TypeOf<
  typeof ZodMatchingContributionConfigCSPCodePerCashSource
>;

const ZodMatchingContributionConfigLimitBracket = z.object({
  contribution_pct: z.number().min(0),
  upper_limit: ZodMatchingContributionLimit,
});

const ZodMatchingContributionConfigBrackets = z
  .array(ZodMatchingContributionConfigLimitBracket)
  .min(EMPLOYEE_SAVINGS_BRACKETS_MIN_NUM)
  .max(EMPLOYEE_SAVINGS_BRACKETS_MAX_NUM)
  .refine(
    (_data) => {
      // Some companies in Axa migration data have MC rules that do not respect this rule
      // for (let idx = 1; idx < data.length; idx++) {
      //   if (data[idx - 1].contribution_pct < data[idx].contribution_pct) {
      //     return false;
      //   }
      // }
      return true;
    },
    {
      message: 'contribution_pct must be descending',
    },
  )
  .refine(
    (data) => {
      const upperLimitType = data[0].upper_limit.type;
      const lastBracketType = data[data.length - 1].upper_limit.type;
      // We accept that the last bracket have limit type percentage_of_legal_cap
      // If that's the case, we ignore it for ascending criteria
      const endOfRange =
        lastBracketType === 'percentage_of_legal_cap' ? data.length - 1 : data.length;
      for (let idx = 1; idx < endOfRange; idx++) {
        if (data[idx].upper_limit.type !== upperLimitType) {
          return false;
        }
        const leftLimit = data[idx - 1].upper_limit;

        const rightLimit = data[idx].upper_limit;

        const leftValue =
          leftLimit.type === 'absolute_amount' ? leftLimit.amount : leftLimit.percentage;

        const rightValue =
          rightLimit.type === 'absolute_amount' ? rightLimit.amount : rightLimit.percentage;

        if (leftValue >= rightValue) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'upper_limit must be ascending and same type',
    },
  );

const ZodMatchingContributionConfigLimitBrackets = z.object({
  type: z.literal('limit_brackets'),
  supported_contribution_types: z
    .array(ZodB2bCashSource)
    .min(1)
    .refine((data) => new Set(data).size === data.length, {
      message: 'must have unique values',
    }),
  brackets: ZodMatchingContributionConfigBrackets,
});

export type MatchingContributionConfigLimitBrackets = z.TypeOf<
  typeof ZodMatchingContributionConfigLimitBrackets
>;

const ZodMatchingContributionConfigLimitBracketsPerCashSource = z.object({
  type: z.literal('limit_brackets_per_cash_source'),
  supported_contribution_types: z
    .array(
      z.object({
        cash_source: ZodB2bCashSource,
        brackets: ZodMatchingContributionConfigBrackets,
      }),
    )
    .min(1)
    .refine((data) => new Set(data.map((entry) => entry.cash_source)).size === data.length, {
      message: 'must have unique values',
    }),
});

export type MatchingContributionConfigLimitBracketsPerCashSource = z.TypeOf<
  typeof ZodMatchingContributionConfigLimitBracketsPerCashSource
>;

const ZodBracketsPerCSPCode = z
  .record(ZodTrimmedString, ZodMatchingContributionConfigBrackets)
  .refine((data) => Object.keys(data).length > 0, {
    message: 'must have at least one contribution type',
  });

const ZodMatchingContributionConfigCSPCodePerCashSourceBrackets = z.object({
  type: z.literal('csp_code_per_cash_source_brackets'),
  supported_contribution_types: z
    .array(
      z.object({
        cash_source: ZodB2bCashSource,
        brackets_per_csp_code: ZodBracketsPerCSPCode,
      }),
    )
    .min(1)
    .refine((data) => new Set(data.map((entry) => entry.cash_source)).size === data.length, {
      message: 'must have unique values',
    }),
});

export type MatchingContributionConfigCSPCodePerCashSourceBrackets = z.TypeOf<
  typeof ZodMatchingContributionConfigCSPCodePerCashSourceBrackets
>;

const ZodMatchingContributionConfigSeniorityBracket = z
  .object({
    upper_limit: ZodMatchingContributionLimit,
    years_in_company_min_inclusive: z.number().nullable(),
    years_in_company_max_exclusive: z.number().nullable(),
  })
  .refine(
    (data) =>
      data.years_in_company_min_inclusive !== null || data.years_in_company_max_exclusive !== null,
    { message: 'either min or max years must be set' },
  )
  .refine(
    (data) =>
      data.years_in_company_min_inclusive === null ||
      data.years_in_company_max_exclusive === null ||
      data.years_in_company_min_inclusive < data.years_in_company_max_exclusive,
    { message: 'min years must be smaller than max years' },
  );

const ZodMatchingContributionConfigSeniorityBrackets = z.object({
  type: z.literal('seniority_brackets'),
  supported_contribution_types: ZodSupportedContributionTypes,
  contribution_pct: z.number(),
  brackets: z
    .array(ZodMatchingContributionConfigSeniorityBracket)
    .min(2)
    .refine(
      (data) => {
        if (!data.length) {
          return false;
        }
        if (data[0].years_in_company_min_inclusive !== null) {
          return false;
        }
        if (data.at(-1)?.years_in_company_max_exclusive !== null) {
          return false;
        }

        for (let idx = 1; idx < data.length; idx++) {
          if (
            data[idx - 1].years_in_company_max_exclusive !==
            data[idx].years_in_company_min_inclusive
          ) {
            return false;
          }
        }

        return true;
      },
      {
        message:
          'first row must have min year set to null, last row must have max year set to null, max must match min of next bracket',
      },
    ),
});

const ZodMatchingContributionConfigCompanySimple = z.object({
  type: z.literal('company_simple'),
  limits_per_contribution_type: z
    .partialRecord(
      ZodB2bCashSource,
      z.object({
        limit: ZodMatchingContributionLimit,
      }),
    )
    .refine((data) => Object.keys(data).length > 0, {
      message: 'must have at least one contribution type',
    }),
});

export type MatchingContributionSeniorityBrackets = z.TypeOf<
  typeof ZodMatchingContributionConfigSeniorityBrackets
>;

export const ZodMatchingContributionConfig = z.discriminatedUnion('type', [
  ZodMatchingContributionConfigSimple,
  ZodMatchingContributionConfigCompanySimple,
  ZodMatchingContributionConfigCSPCode,
  ZodMatchingContributionConfigCSPCodePerCashSource,
  ZodMatchingContributionConfigLimitBrackets,
  ZodMatchingContributionConfigLimitBracketsPerCashSource,
  ZodMatchingContributionConfigCSPCodePerCashSourceBrackets,
  ZodMatchingContributionConfigSeniorityBrackets,
]);

export type MatchingContributionConfig = z.TypeOf<typeof ZodMatchingContributionConfig>;

export const ZodGetPlansMatchingContributionConfigByYearQuery = createZodParamsValidator(
  z.object({
    plan_ids: createZodArrayable(ZodId),
    year: z.number().int(),
  }),
);
export type GetPlansMatchingContributionConfigByYearQuery = z.TypeOf<
  typeof ZodGetPlansMatchingContributionConfigByYearQuery
>;

export const ZodPlanMatchingContributionConfig = z.object({
  plan_id: ZodId,
  year: z.number().int(),
  matching_contribution_computed_by: ZodB2bCampaignActionHandler,
  matching_contribution_config: ZodMatchingContributionConfig.nullable(),
});
export type PlanMatchingContributionConfig = z.TypeOf<typeof ZodPlanMatchingContributionConfig>;

export const ZodGetPlansMatchingContributionConfigByYearResponse = z.array(
  ZodPlanMatchingContributionConfig,
);
export type GetPlansMatchingContributionConfigByYearResponse = z.TypeOf<
  typeof ZodGetPlansMatchingContributionConfigByYearResponse
>;

export const getPlansMatchingContributionConfigByYearEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetPlansMatchingContributionConfigByYearQuery,
  responseBody: ZodGetPlansMatchingContributionConfigByYearResponse,
});
export type GetPlansMatchingContributionConfigByYearEndpoint = GetEndpointType<
  typeof getPlansMatchingContributionConfigByYearEndpoint
>;
